import { useCallback, useEffect, useRef } from 'react'
import { ROOM_EVENT_BATCH_LIMIT, isRoomEventType, type RoomEvent } from '@ai-meeting/shared'
import { appendRoomEvents } from '@/services/room-events'
import { isSharedAssistCard } from '@/pages/Meetings/components/Copilot/assist.types'

/**
 * How often a buffer is handed to the server while a room is talking.
 *
 * Exported because it is also how long a reader has to wait before the room's
 * history can be trusted to hold what was said a moment ago: everything newer
 * than this is still sitting in somebody's buffer. `MeetingChat` reads it to
 * time the second history fetch.
 */
export const ROOM_EVENT_FLUSH_INTERVAL_MS = 3_000

/**
 * How many frames may wait here before the oldest are let go.
 *
 * The buffer only grows while the server is refusing, and it used to grow without limit — which
 * cost memory, and worse, guaranteed the room never recovered: past `ROOM_EVENT_BATCH_LIMIT` every
 * flush was refused for being too long, and a refused flush went straight back into the buffer.
 *
 * Ten batches is roughly a quarter of an hour of a talkative room. Dropping is survivable in a way
 * it would not be elsewhere, because every participant posts the same frames: what this browser
 * lets go of, another one has most likely already reported. The oldest go first for that reason —
 * they have had the longest for somebody else to have got them through.
 */
const MAX_BUFFERED_EVENTS = ROOM_EVENT_BATCH_LIMIT * 10

interface UseRoomEventSyncOptions {
  /** `Meeting.sharedRoomId`, or null for a meeting that shares nothing. */
  sharedRoomId: string | null
  /**
   * This reader's own `Meeting.id`.
   *
   * Only read to tell the two halves of a merge apart — see the note on the
   * buffer clear below. A room named after this meeting is this meeting.
   */
  ownMeetingId: string | null
}

/**
 * Keep the server's copy of a shared room's socket stream up to date (BRAI-539).
 *
 * Runs in every participant's browser at once, on purpose. The alternative —
 * electing one writer — stops recording history the moment that person closes
 * their tab, which is the failure this exists to prevent. Duplicates are
 * expected and the server drops them on a unique key, so no participant has to
 * know who else is listening.
 *
 * Buffers rather than posting per frame: a talkative room produces several
 * events a second, and one request each would be a request storm for data
 * nobody reads until somebody joins late.
 *
 * Failure is swallowed, and the frames are offered again on the next flush —
 * the server ignores whatever it already has, so re-sending costs nothing. What
 * is bounded is how far behind the buffer may fall: see {@link MAX_BUFFERED_EVENTS}.
 *
 * @returns The tap to hand to `useProxySttRecording`'s `onEvent`.
 */
export function useRoomEventSync({
  sharedRoomId,
  ownMeetingId,
}: UseRoomEventSyncOptions): (frame: unknown) => void {
  const pendingRef = useRef<RoomEvent[]>([])
  const inFlightRef = useRef(false)
  const roomRef = useRef(sharedRoomId)

  // A different room is a different history; carrying a buffer over would file
  // the last room's frames under the new one.
  //
  // Not when the room ARRIVES on the machine it is named after. Frames are
  // buffered from the first one, room or no room, and auto-join turns a
  // running meeting into a room — so its first half is sitting in this buffer
  // and nowhere else, and clearing used to throw it away at the instant of
  // joining. It is the room arriving, not a different one.
  //
  // The machine that MOVED into somebody else's room is the other half of
  // that, and its buffer is not the room's. Both were listening to the same
  // call, so those frames are a second transcription of speech the room
  // already holds, stamped against this machine's own clock rather than the
  // room's — posting them gives the room two overlapping copies of its own
  // middle, and gives whoever reads it next no way to tell which is which.
  // Kept on screen, where they are this reader's own record; not published.
  //
  // Leaving and switching still clear, which is what the first arm is for.
  if (roomRef.current !== sharedRoomId) {
    const roomIsThisMeeting = sharedRoomId !== null && sharedRoomId === ownMeetingId
    if (roomRef.current !== null || !roomIsThisMeeting) pendingRef.current = []
    roomRef.current = sharedRoomId
  }

  // No enabled flag: frames only arrive over a live socket, and replayed
  // history comes back over HTTP instead of through here, so there is nothing
  // to switch off.
  const recordEvent = useCallback((frame: unknown) => {
    const event = toRoomEvent(frame)
    if (!event) return
    pendingRef.current.push(event)
    // Trimmed on the way in as well as after a failure: frames keep arriving while the server is
    // unreachable, and this is the path they arrive by.
    if (pendingRef.current.length > MAX_BUFFERED_EVENTS) {
      pendingRef.current = pendingRef.current.slice(-MAX_BUFFERED_EVENTS)
    }
  }, [])

  useEffect(() => {
    if (!sharedRoomId) return undefined

    const flush = () => {
      if (inFlightRef.current || pendingRef.current.length === 0) return
      // Cut at the length the server accepts rather than sending whatever has piled up. A backlog
      // longer than this used to be posted whole and refused as too long — every time, so the
      // backlog never shrank and the room stopped recording for good.
      const batch = pendingRef.current.slice(0, ROOM_EVENT_BATCH_LIMIT)
      pendingRef.current = pendingRef.current.slice(ROOM_EVENT_BATCH_LIMIT)
      inFlightRef.current = true
      // A full batch means more is queued behind it, and a backlog that long
      // only happens when a room arrives on a meeting that has been running —
      // which is the one moment somebody else is reading this room to find out
      // what they missed. At one batch per interval they would be reading a
      // room that is still filling up.
      const hadBacklog = batch.length === ROOM_EVENT_BATCH_LIMIT
      let accepted = false

      void appendRoomEvents(sharedRoomId, batch)
        .then(() => {
          accepted = true
        })
        .catch(() => {
          // Put them back in front of whatever arrived meanwhile, so the room
          // keeps its order.
          const requeued = [...batch, ...pendingRef.current]
          pendingRef.current =
            requeued.length > MAX_BUFFERED_EVENTS
              ? requeued.slice(-MAX_BUFFERED_EVENTS)
              : requeued
        })
        .finally(() => {
          inFlightRef.current = false
          // Only after one the server took. A refusal has just put the batch
          // back, and going straight round again would turn a server that is
          // refusing into a request loop against it.
          if (accepted && hadBacklog) flush()
        })
    }

    // Before the first tick rather than after it. The buffer may already hold
    // everything said before this room existed, and a reader who joined a
    // moment ago is reading the room right now — three seconds is long enough
    // for them to find it empty and have nothing to show for the first half of
    // the meeting.
    flush()
    const timer = setInterval(flush, ROOM_EVENT_FLUSH_INTERVAL_MS)
    return () => {
      clearInterval(timer)
      // One last attempt on the way out: whatever is buffered here has not
      // been reported by this browser, and it may be the only one listening.
      flush()
    }
  }, [sharedRoomId])

  return recordEvent
}

/**
 * The stored form of a frame, or null when the room does not keep this kind.
 *
 * The whole frame becomes the payload — `type` is only `event` hoisted out so
 * the server can index and allow-list on it. Storing the frame whole is what
 * lets replay run it back through the same converter the live path uses.
 */
function toRoomEvent(frame: unknown): RoomEvent | null {
  if (!frame || typeof frame !== 'object') return null
  const type = (frame as { event?: unknown }).event
  if (!isRoomEventType(type)) return null
  if (type === 'assist.card' && !isSharedAssistCard(frame)) return null
  return { type, payload: frame }
}

